import { ExamCourseType } from '@report/graphql';
import { styled, TableCell, TableRow } from '@shared/components';

export interface ExamCourseAnalysisReportTableBodyProps {
  rows: {
    examCode: string;
    examCourseName: string;
    type: ExamCourseType;
    qConfigRef: number;
    totalScore: number;
    sdScore: number;
    maxScore: number;
    minScore: number;
    averageScore: number;
    examinee: {
      inHouse: number | null;
      public: number | null;
      total: number | null;
    };
    examineeUnique: number | null;
    avgAttempts: number | null;
    passedExaminee: {
      inHouse: number | null;
      public: number | null;
      total: number | null;
    };
    failedExaminee: {
      inHouse: number | null;
      public: number | null;
      total: number | null;
    };
    passedRate: {
      inHouse: number | null;
      public: number | null;
      total: number | null;
    };
  }[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body1,
}));

export default function ExamCourseAnalysisReportTableBody(
  props: Readonly<ExamCourseAnalysisReportTableBodyProps>,
) {
  const { rows } = props;

  return (
    <>
      {rows.map((row, rowIndex) => {
        return (
          <TableRow key={row.examCode} sx={{ border: 'none', maxHeight: '40px' }}>
            <StyledTableCell align='center' sx={{ minWidth: '90px' }}>
              {rowIndex + 1}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '140px' }}>
              {row.examCode}
            </StyledTableCell>
            <StyledTableCell align='left' sx={{ minWidth: '440px' }}>
              {row.examCourseName}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '80px' }}>
              {row.type === ExamCourseType.Main ? 'Main' : 'Mock'}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '113px' }}>
              {row.qConfigRef}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '110px' }}>
              {row.totalScore}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '110px' }}>
              {row.sdScore.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '110px' }}>
              {row.maxScore}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ width: '110px' }}>
              {row.minScore}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                width: '110px',
                borderRight: `1px solid ${theme.palette.grey[900]}`,
              })}
            >
              {row.averageScore.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderLeft: `1px solid ${theme.palette.grey[900]}`,
              })}
            >
              {row.examinee.public !== null ? row.examinee.public : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.examinee.inHouse !== null ? row.examinee.inHouse : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderRight: `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: theme.palette.grey[100],
                ...theme.typography.body1Semibold,
              })}
            >
              {row.examinee.total !== null ? row.examinee.total : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.examineeUnique !== null ? row.examineeUnique : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.examineeUnique !== null && row.avgAttempts !== null
                ? `${row.avgAttempts.toFixed(2)}`
                : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderLeft: `1px solid ${theme.palette.grey[900]}`,
              })}
            >
              {row.passedExaminee.public !== null ? row.passedExaminee.public : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.passedExaminee.inHouse !== null ? row.passedExaminee.inHouse : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderRight: `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: theme.palette.grey[100],
                ...theme.typography.body1Semibold,
              })}
            >
              {row.passedExaminee.total !== null ? row.passedExaminee.total : '-'}
            </StyledTableCell>

            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderLeft: `1px solid ${theme.palette.grey[900]}`,
              })}
            >
              {row.failedExaminee.public}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.failedExaminee.inHouse !== null ? row.failedExaminee.inHouse : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderRight: `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: theme.palette.grey[100],
                ...theme.typography.body1Semibold,
              })}
            >
              {row.failedExaminee.total !== null ? row.failedExaminee.total : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderLeft: `1px solid ${theme.palette.grey[900]}`,
              })}
            >
              {row.passedRate.public !== null ? `${row.passedRate.public.toFixed(2)}%` : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '108px',
              }}
            >
              {row.passedRate.inHouse !== null ? `${row.passedRate.inHouse.toFixed(2)}%` : '-'}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={(theme) => ({
                minWidth: '108px',
                borderRight: `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: theme.palette.grey[100],
                ...theme.typography.body1Semibold,
              })}
            >
              {row.passedRate.total !== null ? `${row.passedRate.total.toFixed(2)}%` : '-'}
            </StyledTableCell>
          </TableRow>
        );
      })}
    </>
  );
}
